import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { StateStorage } from 'zustand/middleware'
import { isSignedIn } from '../../api/auth'
import type { QueueSearch } from './searchParams'

/**
 * The operator's last queue view, so opening a job does not throw their filters
 * away (04 §3c).
 *
 * **This is a memory of a URL, not filter state.** HARD RULE 3 puts queue
 * filters in the URL and they stay there: `QueueScreen` reads `useSearch`, the
 * query key derives from the URL, and links stay shareable. This slice exists
 * only so the route can REWRITE a bare `/queue` back to where the operator was.
 * Moving filter reads in here would break shareable links, reload and the query
 * key derivation in one stroke.
 */
interface QueueViewState {
  /** The last search the queue displayed, or null when nothing is remembered. */
  readonly search: QueueSearch | null
  /**
   * A no-op once the session is no longer live (06 §5). `QueueScreen`'s
   * remember-on-search-change effect is the only production caller, and it is
   * not guaranteed to stop the instant a session ends: an unrelated in-flight
   * request 401ing, or `QueueScreen`'s own lazy chunk still resolving, can
   * both land a write here *after* `SessionBridge` has already reacted to
   * `endSession` and cleared this store. Refusing the write here — rather
   * than trusting every caller to check first, or trusting write order to
   * come out right — is what keeps a departing operator's filters from being
   * resurrected for whoever signs in next on the same shared workstation.
   */
  remember: (search: QueueSearch) => void
  clear: () => void
}

export const QUEUE_VIEW_STORAGE_KEY = 'pigeonly.physical-mail.queue-view'

/**
 * `sessionStorage`, wrapped so it can never throw into a navigation.
 *
 * Session scope matches the rest of the app: auth defaults to `sessionStorage`
 * too, with `localStorage` only under "Remember me" (06 §5a). So the view
 * survives a reload and dies with the tab, and the next person at a shared
 * workstation does not inherit it.
 *
 * Every call is guarded: Safari in private mode and a full quota both throw on
 * `setItem`, and a remembered view is a convenience — never a reason to fail a
 * route transition.
 */
export const queueViewStorage: StateStorage = {
  getItem: (name) => {
    try {
      return sessionStorage.getItem(name)
    } catch {
      return null
    }
  },
  setItem: (name, value) => {
    try {
      sessionStorage.setItem(name, value)
    } catch {
      /* a view we cannot remember is not worth an exception */
    }
  },
  removeItem: (name) => {
    try {
      sessionStorage.removeItem(name)
    } catch {
      /* as above */
    }
  },
}

export const useQueueViewStore = create<QueueViewState>()(
  persist(
    (set) => ({
      search: null,
      // Replace, never merge: "Clear all" has to be able to remember emptiness.
      // Refused once signed out — see the `remember` doc comment above.
      remember: (search) => {
        if (!isSignedIn()) return
        set({ search })
      },
      clear: () => set({ search: null }),
    }),
    {
      name: QUEUE_VIEW_STORAGE_KEY,
      storage: createJSONStorage(() => queueViewStorage),
    },
  ),
)
